import { ProductThumbnailImage } from "@/components/product/ProductThumbnailImage";
import { LiveProvenanceBadge } from "@/components/provenance/LiveProvenanceBadge";
import { QuantityControl } from "@/components/ui/QuantityControl";
import { useRemoveCartItem, useUpdateCartItem } from "@/queries/cart";
import { resolveLineItemDisplay } from "@/utils/cart";
import { formatPrice } from "@/utils/money";
import type { SalonStockerHttpTypes } from "@repo/api-sdk";
import { TruncateTooltip } from "@repo/design-system";
import { cn } from "@repo/design-system/utils";
import { memo, useCallback, useState } from "react";
import { VariantPicker, type VariantChoice } from "./VariantPicker";

export interface CartLineItemProps {
  /**
   * The line item data
   */
  item: SalonStockerHttpTypes.StoreSellerCartLineItem;
  /**
   * The cart ID for mutations
   */
  cartId: string;
  /**
   * Supplier this item is bought from. Shown in the flat "All Items" list,
   * where the per-supplier grouping that would otherwise name it is absent.
   * Omit inside a supplier-grouped view - the accordion header already says
   * it, and repeating it on every row is noise.
   */
  sellerName?: string;
  /**
   * Supplier to name in the provenance tooltip. Separate from `sellerName`
   * because that prop decides whether to *print* a supplier line, and a
   * supplier-grouped view wants the name in the tooltip while suppressing the
   * visible line - without this the badge would say "the supplier" two rows
   * above a shipping badge naming the seller outright. Falls back to
   * `sellerName` for the flat list, where the two coincide.
   */
  provenanceSource?: string;
  /**
   * Size variant
   */
  size?: "sm" | "md";
  /**
   * Whether to show the product image
   */
  showImage?: boolean;
  /**
   * Small label shown beside the product name, e.g. marking a line the
   * salon's rep added rather than one from the catalogue.
   */
  label?: string;
  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * Displays a single cart line item with quantity controls.
 * Uses mutations for updating quantity and removing items.
 */
export const CartLineItem = memo(function CartLineItem({
  item,
  cartId,
  sellerName,
  provenanceSource,
  label,
  size = "md",
  showImage = true,
  className
}: CartLineItemProps) {
  const { setQuantity, flush: flushQuantity } = useUpdateCartItem(cartId);
  const removeMutation = useRemoveCartItem(cartId);

  // Owned by the picker (its hooks only exist on lines that have one), reported
  // up so the whole row can lock around a switch - from the moment an option is
  // chosen, not from when its request goes out.
  const [isChangingVariant, setIsChangingVariant] = useState(false);

  // A removal or a variant switch locks the row. Quantity edits are debounced
  // and applied to the cache on the click, so blocking on their in-flight
  // request would drop exactly the rapid taps the debounce exists to collect.
  // A switch is the opposite case: it replaces the line a quantity edit would
  // be addressed to, so edits during it have nothing valid to land on.
  const isPending = removeMutation.isPending || isChangingVariant;

  // One owner for which of the four candidate strings actually get printed -
  // suppliers populate them inconsistently, so every line is deduped against
  // the others rather than trusting a fixed field mapping.
  const { productTitle, subtitle, variantTitle } = resolveLineItemDisplay(
    item,
    sellerName
  );

  // Present only on lines an operator quoted; absent on ones the salon added
  // themselves, which have no quote to have moved from.
  const quoteDiff = item.quote_diff;

  // The framework types the flag as a required boolean, but the strict
  // `=== true` still guards a response from an older backend that predates the
  // field - missing means no picker. The flag is true for eligible ad hoc lines
  // too (KAN-398): they switch by Shopify variant instead of by offer.
  const canChangeVariant = item.variant_change_available === true;

  // What the line is on, for a variant-options response that flags no option
  // as current: the offer for an offer line (when the metadata carries it), the
  // Shopify variant for an ad hoc line.
  const lineChoice: VariantChoice | undefined =
    item.ad_hoc !== undefined && item.ad_hoc !== null
      ? { shopify_variant_id: item.ad_hoc.shopify_variant_id }
      : typeof item.metadata?.offer_id === "string"
        ? { offer_id: item.metadata.offer_id }
        : undefined;

  const handleBeforeVariantChange = useCallback(
    () => flushQuantity(item.id),
    [item.id, flushQuantity]
  );

  const handleQuantityChange = useCallback(
    (newQuantity: number) => {
      // A switch is replacing this line; an edit now would be addressed to an
      // id that is about to stop existing.
      if (isChangingVariant) return;
      setQuantity(item.id, newQuantity);
    },
    [item.id, setQuantity, isChangingVariant]
  );

  // Destructured so this callback doesn't key on the mutation object, which
  // useMutation rebuilds every render.
  const { mutateAsync: removeItem } = removeMutation;
  const handleRemove = useCallback(async () => {
    if (isPending) return;

    await removeItem(item.id);
  }, [item.id, removeItem, isPending]);

  const isSm = size === "sm";

  return (
    <div
      className={cn(
        "flex gap-3",
        isPending && "pointer-events-none opacity-50",
        // Tighter rows on a phone; the roomier md spacing returns from sm.
        isSm ? "py-2" : "py-2 sm:py-3",
        className
      )}
    >
      {/* Thumbnail */}
      {showImage && (
        <div
          className={cn(
            "flex-shrink-0 overflow-hidden rounded-md bg-gray-100",
            isSm ? "h-12 w-12" : "h-16 w-16"
          )}
        >
          <ProductThumbnailImage
            src={item.thumbnail ?? null}
            alt={item.title}
            className="h-full w-full object-cover"
            fallback={
              <div className="flex h-full w-full items-center justify-center text-gray-400">
                <span className={isSm ? "text-xs" : "text-sm"}>No image</span>
              </div>
            }
          />
        </div>
      )}

      {/* Content */}
      <div className="flex min-w-0 flex-1 flex-col justify-between">
        {/* Title row - subtotal sits top-right, on the same edge as the
            quantity control below it (matches the order summary's rows). */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            {/* Product name only - the variant is named on its own line
                below, and `item.title` would repeat it here. */}
            <div className="flex min-w-0 items-center gap-2">
              <TruncateTooltip
                as="h4"
                className={cn(
                  "min-w-0 font-medium text-gray-900",
                  isSm ? "text-xs" : "text-sm"
                )}
              >
                {productTitle}
              </TruncateTooltip>
              {label && (
                <span className="flex-shrink-0 text-[11px] font-medium text-gray-400">
                  {label}
                </span>
              )}
            </div>

            {/* Subtitle / Brand */}
            {subtitle && (
              <TruncateTooltip
                as="p"
                className={cn(
                  "text-gray-500",
                  isSm ? "text-[10px]" : "text-xs"
                )}
              >
                {subtitle}
              </TruncateTooltip>
            )}

            {/* Variant - always named when the product actually has options,
                never when it is only Medusa's placeholder variant. */}
            {canChangeVariant && (
              <VariantPicker
                cartId={cartId}
                lineItemId={item.id}
                productTitle={productTitle}
                currentLabel={variantTitle}
                currentSku={item.variant_sku}
                lineChoice={lineChoice}
                size={size}
                onBeforeChange={handleBeforeVariantChange}
                onPendingChange={setIsChangingVariant}
              />
            )}
            {variantTitle && !canChangeVariant && (
              <TruncateTooltip
                as="p"
                className={cn(
                  "text-gray-500",
                  isSm ? "text-[10px]" : "text-xs"
                )}
              >
                {variantTitle}
              </TruncateTooltip>
            )}

            {/* Supplier - only in the flat list, where nothing else names it.
                Lightest of the three lines: it is context, not identity. */}
            {sellerName && (
              <TruncateTooltip
                as="p"
                className={cn(
                  "text-gray-400",
                  isSm ? "text-[10px]" : "text-xs"
                )}
              >
                {sellerName}
              </TruncateTooltip>
            )}
          </div>

          {/* Price, with its "came straight from the supplier" marker hung
              beneath it (KAN-105). Under rather than beside: the price is
              what the eye comes here for, and a chip on its left would push
              it off the right edge the quantity control below aligns to. */}
          <span className="flex flex-shrink-0 flex-col items-end gap-1">
            <span
              className={cn(
                "font-semibold text-gray-900",
                isSm ? "text-xs" : "text-sm"
              )}
            >
              {/* `total` is optional in the API type - Medusa allows a query
                  to omit totals via field selection - but nothing in this app
                  requests a line item without them, so `?? 0` guards that
                  general contract rather than a state this render expects to
                  actually hit. Matches the same fallback in
                  `queries/cart/cache.ts`. */}
              {formatPrice(item.total ?? 0)}
            </span>
            {!item.ad_hoc && (
              <LiveProvenanceBadge
                provenance={item.provenance}
                subject="price"
                source={provenanceSource ?? sellerName}
              />
            )}
          </span>
        </div>

        {/* What moved since this line was quoted, when it was. Only rendered
            on lines that actually changed - an "unchanged" badge on every row
            would drown out the ones that need reading (KAN-39). */}
        {quoteDiff && quoteDiff.status !== "available" && (
          <p
            className={cn(
              "mt-1 font-medium",
              quoteDiff.status === "unavailable"
                ? "text-red-600"
                : "text-amber-700",
              isSm ? "text-[10px]" : "text-xs"
            )}
          >
            {quoteDiff.status === "unavailable" ? (
              "No longer available since your quote"
            ) : (
              <>
                Price changed:{" "}
                <span className="line-through">
                  {formatPrice(quoteDiff.old_price)}
                </span>{" "}
                → {formatPrice(quoteDiff.new_price ?? item.unit_price)}
              </>
            )}
          </p>
        )}

        {/* Unit price and quantity row. `justify-end` (not `justify-between`)
            keeps the control right-aligned at quantity 1, where the unit price
            isn't rendered. */}
        <div className="mt-1 flex items-center justify-end gap-2">
          {item.quantity > 1 && (
            <span
              className={cn(
                "mr-auto text-gray-500",
                isSm ? "text-[10px]" : "text-xs"
              )}
            >
              {formatPrice(item.unit_price)} each
            </span>
          )}

          <QuantityControl
            quantity={item.quantity}
            onQuantityChange={handleQuantityChange}
            onRemove={handleRemove}
            size={isSm ? "sm" : "md"}
            min={1}
            disabled={isChangingVariant}
          />
        </div>
      </div>
    </div>
  );
});
